import { useState, useEffect } from "react";
import { Bell, Plus, Clock, Droplets, Dumbbell, Apple, Moon, X, Check, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Navbar } from "../components/Navbar";
import { ProtectedRoute } from "../components/ProtectedRoute";
import api from "../lib/api";

const REMINDER_TYPES = [
  { value: "meal", label: "Meal", icon: Apple, color: "bg-green-50 text-green-600" },
  { value: "workout", label: "Workout", icon: Dumbbell, color: "bg-orange-50 text-orange-600" },
  { value: "water", label: "Water Intake", icon: Droplets, color: "bg-blue-50 text-blue-600" },
  { value: "rest", label: "Rest Day", icon: Moon, color: "bg-purple-50 text-purple-600" },
  { value: "custom", label: "Custom", icon: Bell, color: "bg-slate-50 text-slate-600" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const RemindersContent = () => {
  const [reminders, setReminders] = useState([]);
  const [todayReminders, setTodayReminders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newReminder, setNewReminder] = useState({
    title: "", reminder_type: "meal", time: "08:00", days: [...DAYS], enabled: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, todayRes] = await Promise.all([
        api.get("/reminders").catch(() => ({ data: [] })),
        api.get("/reminders/today").catch(() => ({ data: [] })),
      ]);
      setReminders(allRes.data || []);
      setTodayReminders(todayRes.data || []);
    } catch (err) { console.error('Error:', err.message || err); }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!newReminder.title) return;
    try {
      await api.post("/reminders", newReminder);
      setShowCreate(false);
      setNewReminder({ title: "", reminder_type: "meal", time: "08:00", days: [...DAYS], enabled: true });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const toggleEnabled = async (rem) => {
    try {
      await api.put(`/reminders/${rem.reminder_id}`, { enabled: !rem.enabled });
      fetchData();
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const deleteReminder = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      fetchData();
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const toggleDay = (day) => {
    setNewReminder(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day]
    }));
  };

  const getTypeInfo = (type) => REMINDER_TYPES.find(t => t.value === type) || REMINDER_TYPES[4];

  const seedDefaults = async () => {
    const defaults = [
      { title: "Morning Breakfast", reminder_type: "meal", time: "07:30", days: DAYS },
      { title: "Mid-Morning Water", reminder_type: "water", time: "10:00", days: DAYS },
      { title: "Lunch Time", reminder_type: "meal", time: "12:30", days: DAYS },
      { title: "Afternoon Workout", reminder_type: "workout", time: "16:00", days: ["Mon", "Wed", "Fri"] },
      { title: "Afternoon Water", reminder_type: "water", time: "15:00", days: DAYS },
      { title: "Dinner Time", reminder_type: "meal", time: "19:00", days: DAYS },
      { title: "Evening Water", reminder_type: "water", time: "20:00", days: DAYS },
      { title: "Rest & Recovery", reminder_type: "rest", time: "21:30", days: ["Tue", "Thu", "Sun"] },
    ];
    for (const d of defaults) {
      try { await api.post("/reminders", { ...d, enabled: true }); } catch (err) { console.error('Error:', err.message || err); }
    }
    fetchData();
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="reminders-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-[#1B3A5C] p-2.5 rounded-xl"><Bell className="h-6 w-6 text-white" /></div>
              <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0F172A]">Health Reminders</h1>
            </div>
            <p className="text-slate-500 ml-14">Automated reminders for meals, workouts & water</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="add-reminder-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 font-semibold shadow-lg transition-all">
            <Plus className="mr-2 h-4 w-4" /> Add Reminder
          </Button>
        </div>

        {/* Today's Schedule */}
        <div className="mb-8">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-4">Today's Schedule</h2>
          {todayReminders.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-2xl p-10 shadow-sm text-center">
              <Bell className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">No reminders for today</p>
              <Button onClick={seedDefaults} data-testid="seed-reminders-btn" variant="outline" className="rounded-full border-[#0B6E4F] text-[#0B6E4F]">
                Set Up Default Reminders
              </Button>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-100" />
              <div className="space-y-4">
                {todayReminders.map((rem, i) => {
                  const info = getTypeInfo(rem.reminder_type);
                  const Icon = info.icon;
                  return (
                    <div key={i} className="flex items-center gap-4 pl-4" data-testid={`today-reminder-${i}`}>
                      <div className={`w-5 h-5 rounded-full border-2 border-[#0B6E4F] bg-white z-10 flex items-center justify-center`}>
                        <div className="w-2 h-2 rounded-full bg-[#0B6E4F]" />
                      </div>
                      <div className="flex-1 bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${info.color.split(" ")[0]}`}>
                          <Icon className={`h-4 w-4 ${info.color.split(" ")[1]}`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#0F172A]">{rem.title}</p>
                          <p className="text-xs text-slate-400 capitalize">{rem.reminder_type}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-[#0B6E4F]">{rem.time}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* All Reminders */}
        <h2 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-4">All Reminders</h2>
        {reminders.length === 0 && !loading ? (
          <p className="text-sm text-slate-400 text-center py-8">No reminders created yet</p>
        ) : (
          <div className="space-y-3">
            {reminders.map((rem, i) => {
              const info = getTypeInfo(rem.reminder_type);
              const Icon = info.icon;
              return (
                <div key={i} className={`bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex items-center gap-4 ${!rem.enabled ? "opacity-50" : ""}`} data-testid={`reminder-${rem.reminder_id}`}>
                  <div className={`p-2.5 rounded-xl ${info.color.split(" ")[0]}`}>
                    <Icon className={`h-5 w-5 ${info.color.split(" ")[1]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#0F172A]">{rem.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#0B6E4F] font-medium">{rem.time}</span>
                      <span className="text-xs text-slate-300">|</span>
                      <span className="text-xs text-slate-400">{rem.days?.join(", ")}</span>
                    </div>
                  </div>
                  <Switch checked={rem.enabled} onCheckedChange={() => toggleEnabled(rem)} data-testid={`toggle-reminder-${rem.reminder_id}`} />
                  <button onClick={() => deleteReminder(rem.reminder_id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-reminder-${rem.reminder_id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Reminder Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-reminder-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Outfit'] text-lg font-semibold">New Reminder</h3>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <Input value={newReminder.title} onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })} placeholder="Morning Workout" className="mt-1 h-11 rounded-xl bg-slate-50" data-testid="reminder-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Type</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {REMINDER_TYPES.map(t => (
                      <button key={t.value} onClick={() => setNewReminder({ ...newReminder, reminder_type: t.value })}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors ${newReminder.reminder_type === t.value ? "bg-[#0B6E4F] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        <t.icon className="h-3.5 w-3.5" />{t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Time</Label>
                  <Input type="time" value={newReminder.time} onChange={(e) => setNewReminder({ ...newReminder, time: e.target.value })} className="mt-1 h-11 rounded-xl bg-slate-50" data-testid="reminder-time-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Days</Label>
                  <div className="flex gap-1.5 mt-1">
                    {DAYS.map(d => (
                      <button key={d} onClick={() => toggleDay(d)}
                        className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors ${newReminder.days.includes(d) ? "bg-[#0B6E4F] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="submit-reminder-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6">Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Reminders() {
  return <ProtectedRoute><RemindersContent /></ProtectedRoute>;
}
